import React from 'react';

import { useIntl } from 'react-intl';

import { GET_PAY_STATION_RESOURCE } from 'src/features/payment/api/useGetPayStation';
import { LIST_PAY_STATIONS_RESOURCE } from 'src/features/payment/api/useListPayStations';
import { useUpdatePayStation } from 'src/features/payment/api/useUpdatePayStation';
import PayStationFormModal from 'src/features/payment/components/forms/PayStationFormModal';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStation } from 'src/features/payment/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PayStationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    children?: React.ReactNode;
}

function UpdatePayStationFormModal<TData>({
    className,
    semantics,
    children,
    selectedPayStation,
}: PayStationFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate, isPending } = useUpdatePayStation({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_PAY_STATION_RESOURCE] });
            toast({
                title: formatMessage(paymentsMessages.updatePayStationSuccess),
                description: formatMessage(paymentsMessages.updatePayStationSuccessDescription, {
                    entry: success.name,
                }),
            });
        },
    });

    return (
        <PayStationFormModal
            selectedPayStation={selectedPayStation}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ parkingId, data, payStationId: selectedPayStation.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(paymentsMessages.editPayStationSubtitle, {
                entry: selectedPayStation.name,
            })}
        >
            {children}
        </PayStationFormModal>
    );
}

export default UpdatePayStationFormModal;
